import type {
  ConnectStripeRequest,
  CreateCustomerRequest,
  CreateProductRequest,
  CreateVariantRequest,
  CustomerDetail,
  CustomerSummary,
  OrderDetail,
  OrderSummary,
  PaymentGatewayStatus,
  LoginRequest,
  LoginResponse,
  MeResponse,
  MembershipSummary,
  SignupRequest,
  SlugAvailability,
  StoreSettings,
  ProductDetail,
  ProductSummary,
  UpdateProductRequest,
  UpdateStoreRequest,
  UpdateVariantRequest,
  VariantSummary,
} from '@upweo/types';

import { apiRequest } from './api-client.js';

/** Typed wrappers over the Admin REST API. One function per endpoint. */

export const login = (body: LoginRequest): Promise<LoginResponse> =>
  apiRequest<LoginResponse>('/auth/login', { method: 'POST', body });

export const logout = (): Promise<{ success: boolean }> =>
  apiRequest<{ success: boolean }>('/auth/logout', { method: 'POST' });

export const me = (): Promise<MeResponse> => apiRequest<MeResponse>('/auth/me');

export const listStores = (): Promise<readonly MembershipSummary[]> =>
  apiRequest<readonly MembershipSummary[]>('/tenants');

export const switchStore = (tenantId: string): Promise<{ tenantId: string }> =>
  apiRequest<{ tenantId: string }>('/tenants/switch', { method: 'POST', body: { tenantId } });

export interface ProductPage {
  readonly items: readonly ProductSummary[];
  readonly nextCursor: string | null;
  readonly hasNextPage: boolean;
}

export const listProducts = (params: {
  search?: string;
  status?: string;
  signal?: AbortSignal;
}): Promise<ProductPage> => {
  const query = new URLSearchParams();
  if (params.search) {
    query.set('search', params.search);
  }
  if (params.status) {
    query.set('status', params.status);
  }
  const qs = query.toString();
  return apiRequest<ProductPage>(`/products${qs ? `?${qs}` : ''}`, {
    ...(params.signal ? { signal: params.signal } : {}),
  });
};

export const getProduct = (id: string): Promise<ProductDetail> =>
  apiRequest<ProductDetail>(`/products/${id}`);

export const createProduct = (body: CreateProductRequest): Promise<ProductDetail> =>
  apiRequest<ProductDetail>('/products', { method: 'POST', body });

export const updateProduct = (id: string, body: UpdateProductRequest): Promise<ProductDetail> =>
  apiRequest<ProductDetail>(`/products/${id}`, { method: 'PATCH', body });

export const deleteProduct = (id: string): Promise<{ success: boolean }> =>
  apiRequest<{ success: boolean }>(`/products/${id}`, { method: 'DELETE' });

/** Money arrives as a decimal string; format it without ever parsing to float for maths. */
export const formatMoney = (amount: string, currencyCode: string): string =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(
    Number(amount),
  );

// ── Orders (read-only) ──────────────────────────────────────────────────────

export interface OrderPage {
  readonly items: readonly OrderSummary[];
  readonly nextCursor: string | null;
  readonly hasNextPage: boolean;
}

export const listOrders = (params: {
  search?: string;
  signal?: AbortSignal;
}): Promise<OrderPage> => {
  const query = new URLSearchParams();
  if (params.search) {
    query.set('search', params.search);
  }
  const qs = query.toString();
  return apiRequest<OrderPage>(`/orders${qs ? `?${qs}` : ''}`, {
    ...(params.signal ? { signal: params.signal } : {}),
  });
};

export const getOrder = (id: string): Promise<OrderDetail> =>
  apiRequest<OrderDetail>(`/orders/${id}`);

// ── Customers ───────────────────────────────────────────────────────────────

export interface CustomerPage {
  readonly items: readonly CustomerSummary[];
  readonly nextCursor: string | null;
  readonly hasNextPage: boolean;
}

export const listCustomers = (params: {
  search?: string;
  signal?: AbortSignal;
}): Promise<CustomerPage> => {
  const query = new URLSearchParams();
  if (params.search) {
    query.set('search', params.search);
  }
  const qs = query.toString();
  return apiRequest<CustomerPage>(`/customers${qs ? `?${qs}` : ''}`, {
    ...(params.signal ? { signal: params.signal } : {}),
  });
};

export const getCustomer = (id: string): Promise<CustomerDetail> =>
  apiRequest<CustomerDetail>(`/customers/${id}`);

export const createCustomer = (body: CreateCustomerRequest): Promise<CustomerDetail> =>
  apiRequest<CustomerDetail>('/customers', { method: 'POST', body });

/** Short, locale-aware date for tables. */
export const formatDate = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );

// ── Variants ────────────────────────────────────────────────────────────────

export const createVariant = (
  productId: string,
  body: CreateVariantRequest,
): Promise<VariantSummary> =>
  apiRequest<VariantSummary>(`/products/${productId}/variants`, { method: 'POST', body });

export const updateVariant = (
  productId: string,
  variantId: string,
  body: UpdateVariantRequest,
): Promise<VariantSummary> =>
  apiRequest<VariantSummary>(`/products/${productId}/variants/${variantId}`, {
    method: 'PATCH',
    body,
  });

export const adjustInventory = (
  productId: string,
  variantId: string,
  onHand: number,
): Promise<VariantSummary> =>
  apiRequest<VariantSummary>(`/products/${productId}/variants/${variantId}/inventory`, {
    method: 'PATCH',
    body: { onHand },
  });

export const deleteVariant = (
  productId: string,
  variantId: string,
): Promise<{ success: boolean }> =>
  apiRequest<{ success: boolean }>(`/products/${productId}/variants/${variantId}`, {
    method: 'DELETE',
  });

// ── Store hosting ───────────────────────────────────────────────────────────

export const signup = (body: SignupRequest): Promise<LoginResponse> =>
  apiRequest<LoginResponse>('/auth/signup', { method: 'POST', body });

export const checkSlug = (slug: string, signal?: AbortSignal): Promise<SlugAvailability> =>
  apiRequest<SlugAvailability>(`/auth/signup/check-slug?slug=${encodeURIComponent(slug)}`, {
    ...(signal ? { signal } : {}),
  });

export const getStore = (): Promise<StoreSettings> => apiRequest<StoreSettings>('/tenants/current');

export const updateStore = (body: UpdateStoreRequest): Promise<StoreSettings> =>
  apiRequest<StoreSettings>('/tenants/current', { method: 'PATCH', body });

/** Save the signed-in user's language so the emails we send them match it. */
export const updateMyLocale = (locale: string): Promise<{ updated: boolean }> =>
  apiRequest<{ updated: boolean }>('/auth/me', { method: 'PATCH', body: { locale } });

/**
 * Payment gateway settings.
 *
 * The response never contains a usable credential — only a masked key — so it
 * is safe to hold in query cache like any other settings object.
 */
export const getPaymentGateway = (): Promise<PaymentGatewayStatus> =>
  apiRequest<PaymentGatewayStatus>('/payments/gateway');

export const connectStripe = (body: ConnectStripeRequest): Promise<PaymentGatewayStatus> =>
  apiRequest<PaymentGatewayStatus>('/payments/gateway/stripe', { method: 'PUT', body });

export const disconnectStripe = (): Promise<PaymentGatewayStatus> =>
  apiRequest<PaymentGatewayStatus>('/payments/gateway/stripe', { method: 'DELETE' });

export const createStore = (body: {
  storeName: string;
  currencyCode?: string;
  countryCode?: string;
}): Promise<StoreSettings> => apiRequest<StoreSettings>('/tenants', { method: 'POST', body });
